/**
 * What a new Team's create brought in from the tracker, as one sentence on
 * its queue. The Add a team sheet hands the 201's `setup.imported` to the
 * queue as navigation state (`location.state.imported`).
 */
import type { TeamTracker } from "./teamsApi.js";

/** The create's import result, as the 201 carries it. */
export interface ImportOutcome {
  imported?: unknown[];
  failed?: Array<{ ticketKey?: unknown }>;
  skipped?: unknown;
}

export function trackerName(trackers: readonly TeamTracker[]): string | null {
  if (trackers.length !== 1) return null;
  return trackers[0] === "jira" ? "Jira" : "Linear";
}

/** The outcome from navigation state, or null when there is none to read. */
export function importOutcomeOf(state: unknown): ImportOutcome | null {
  if (!state || typeof state !== "object") return null;
  const imported = (state as { imported?: unknown }).imported;
  return imported && typeof imported === "object" ? (imported as ImportOutcome) : null;
}

/**
 * One sentence about the import, or null when there is nothing to say (it
 * all landed, or there is no tracker to name).
 */
export function importOutcomeLine(imported: ImportOutcome | null, tracker: string | null): string | null {
  if (!imported) return null;
  if (imported.skipped === "no_tickets") {
    return tracker
      ? `No open tickets are assigned to you in ${tracker}. Assign some to yourself there, or add one with Add tickets.`
      : null;
  }
  if (imported.skipped === "no_repo") {
    return "The Team has no repository to build in yet. Add one in Team settings.";
  }
  if (imported.skipped === "error") {
    return `${tracker ?? "Your tracker"} did not answer while your tickets were coming in. Bailey tries again weekdays at 9, or add tickets now.`;
  }
  const failed = (imported.failed ?? [])
    .map((entry) => entry?.ticketKey)
    .filter((key): key is string => typeof key === "string");
  if ((imported.imported ?? []).length === 0 && failed.length > 0) {
    const names = failed.length === 1 ? failed[0] : `${failed.slice(0, -1).join(", ")} and ${failed.at(-1)}`;
    return `${names} did not come in. Add ${failed.length === 1 ? "it" : "them"} with Add tickets.`;
  }
  return null;
}
